import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';

import { NamedLink } from '../../../../components';
import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionColumns.module.css';

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionColumns = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  const [postHeroTab, setPostHeroTab] = useState('parent');

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {sectionId === 'post-hero' ? (
        <div className="postHeroActions">
          <p>
            Vetted providers. Transparent booking. Support for every part of your recovery all in
            one place.
          </p>
          <div className="postHeroTabs" role="tablist" aria-label="Select user type">
            <button
              className={classNames('postHeroTab', { postHeroTabActive: postHeroTab === 'parent' })}
              type="button"
              role="tab"
              aria-selected={postHeroTab === 'parent'}
              onClick={() => setPostHeroTab('parent')}
            >
              <FormattedMessage id="PostHeroTabs.parent" defaultMessage="I'm a parent" />
            </button>
            <button
              className={classNames('postHeroTab', {
                postHeroTabActive: postHeroTab === 'provider',
              })}
              type="button"
              role="tab"
              aria-selected={postHeroTab === 'provider'}
              onClick={() => setPostHeroTab('provider')}
            >
              <FormattedMessage id="PostHeroTabs.provider" defaultMessage="I'm a provider" />
            </button>
          </div>

          <div className="postHeroCtas">
            {postHeroTab === 'parent' ? (
              <>
                <NamedLink
                  className={classNames('postHeroCta', 'postHeroCtaPrimary')}
                  name="SearchPage"
                >
                  <FormattedMessage
                    id="PostHeroTabs.parentCtaPrimary"
                    defaultMessage="Find a provider"
                  />
                </NamedLink>
                <NamedLink
                  className={classNames('postHeroCta', 'postHeroCtaSecondary')}
                  name="LandingPage"
                  to={{ hash: '#find-the-support' }}
                >
                  <FormattedMessage
                    id="PostHeroTabs.parentCtaSecondary"
                    defaultMessage="Browse services"
                  />
                </NamedLink>
              </>
            ) : (
              <>
                <NamedLink
                  className={classNames('postHeroCta', 'postHeroCtaPrimary')}
                  name="SignupForUserTypePage"
                  params={{ userType: 'provider' }}
                >
                  <FormattedMessage
                    id="PostHeroTabs.providerCtaPrimary"
                    defaultMessage="Join free today"
                  />
                </NamedLink>
                <NamedLink
                  className={classNames('postHeroCta', 'postHeroCtaSecondary')}
                  name="LandingPage"
                  to={{ hash: '#how-it-works-provider' }}
                >
                  <FormattedMessage
                    id="PostHeroTabs.providerCtaSecondary"
                    defaultMessage="Learn more"
                  />
                </NamedLink>
              </>
            )}
          </div>
        </div>
      ) : null}
      {hasBlocks ? (
        <div
          className={classNames(defaultClasses.blockContainer, getColumnCSS(numColumns), {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          <BlockBuilder
            ctaButtonClass={defaultClasses.ctaButton}
            blocks={blocks}
            sectionId={sectionId}
            responsiveImageSizes={getResponsiveImageSizes(numColumns)}
            options={options}
          />
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionColumns;
